import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import WaitlistForm from "@/components/WaitlistForm";
import TextMeLinkForm from "@/components/TextMeLinkForm";
import { ArrowRight, CheckCircle2, Loader2, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "/logo-brand.png";

export default function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("token");
  const [plan, setPlan] = useState(null);
  const [seats, setSeats] = useState(null);
  const [invitePrefilled, setInvitePrefilled] = useState(false);
  const [form, setForm] = useState({
    dealership_name: "",
    admin_name: "",
    admin_email: "",
    admin_password: "",
    dealership_phone: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/billing/plans`).then((r) => {
      setPlan(r.data.plans?.[0] || null);
    }).catch(() => {});
    axios.get(`${API}/api/billing/seats-remaining`).then((r) => {
      setSeats(r.data);
    }).catch(() => {});
    if (inviteToken) {
      axios.get(`${API}/api/waitlist/invite/${encodeURIComponent(inviteToken)}`)
        .then((r) => {
          const d = r.data || {};
          setForm((f) => ({
            ...f,
            dealership_name: d.dealership_name || f.dealership_name,
            admin_name: d.admin_name || f.admin_name,
            admin_email: d.admin_email || f.admin_email,
            dealership_phone: d.dealership_phone || f.dealership_phone,
          }));
          setInvitePrefilled(true);
        })
        .catch(() => {
          toast.error("Invite link expired or invalid — filling in from scratch");
        });
    }
  }, [inviteToken]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // 1. Create the pending tenant + admin user
      const signup = await axios.post(`${API}/api/tenants/signup`, {
        dealership_name: form.dealership_name.trim(),
        admin_email: form.admin_email.trim().toLowerCase(),
        admin_password: form.admin_password,
        admin_name: form.admin_name.trim() || null,
        dealership_phone: form.dealership_phone.trim() || null,
      });
      const tenant_id = signup.data.tenant_id;

      // 2. Kick off Stripe checkout
      const checkout = await axios.post(`${API}/api/payments/checkout`, {
        tenant_id,
        origin_url: window.location.origin,
        lookup_key: "founding_four_monthly",
      });
      window.location.href = checkout.data.checkout_url;
    } catch (err) {
      setBusy(false);
      toast.error(err.response?.data?.detail || "Signup failed");
    }
  };

  const price = plan ? plan.amount_display : "$1,495/month";

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="w-full bg-navy text-white text-[0.65rem] font-mono-label text-center py-1.5">
        © {new Date().getFullYear()} Decline Drive™ by Ayers Enterprises. All rights reserved.
      </div>
      <div className="grid md:grid-cols-2 flex-1">
        <div className="flex items-center justify-center p-8">
          {seats?.sold_out ? (
            <WaitlistForm price={price} />
          ) : (
          <form onSubmit={onSubmit} className="w-full max-w-md space-y-5" data-testid="signup-form">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-1.5 bg-[hsl(var(--gold))] rounded-lg shadow-md">
                <img src={LOGO_URL} alt="Decline Drive" className="w-20 h-20 object-contain" />
              </div>
              <div>
                <div className="font-display font-black text-xl leading-none text-navy">Decline Drive</div>
                <div className="gold-divider mt-2 mb-2" />
                <div className="font-mono-label text-foreground/70 text-[0.6rem]">
                  Ayers Enterprises
                </div>
              </div>
            </div>

            <div>
              <h1 className="font-display text-4xl font-bold tracking-tighter">Start your 60-day guarantee</h1>
              <p className="text-sm text-muted-foreground mt-2">
                One of only <span className="font-semibold text-navy">four seats</span> — locked-in {price} for the life of your contract.
              </p>
            </div>

            {invitePrefilled && (
              <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm flex items-start gap-2" data-testid="signup-invite-prefill">
                <Sparkles className="w-4 h-4 text-emerald-700 mt-0.5" />
                <div className="text-emerald-900">
                  <div className="font-semibold">Waitlist invite unlocked</div>
                  <div className="text-xs text-emerald-800/80 mt-0.5">Filled in your details from the invite email — set a password and you're 3 clicks from checkout.</div>
                </div>
              </div>
            )}

            <div className="rounded-lg border border-[hsl(var(--gold))] bg-[hsl(var(--gold-light))]/10 p-3 text-sm">
              <div className="flex items-center justify-between gap-2">
                <div className="font-mono-label text-[hsl(var(--gold-dark))] text-[0.65rem] tracking-widest">FOUNDING FOUR RATE</div>
                {seats && (
                  <div
                    className={`text-[0.65rem] font-mono-label tracking-widest px-2 py-0.5 rounded-full ${
                      seats.sold_out
                        ? "bg-red-600 text-white"
                        : seats.remaining <= 1
                          ? "bg-red-500/15 text-red-700 border border-red-500/40 animate-pulse"
                          : "bg-navy text-white"
                    }`}
                    data-testid="signup-seats-remaining"
                  >
                    {seats.sold_out
                      ? "SOLD OUT · WAITLIST ONLY"
                      : `${seats.claimed} of ${seats.total} CLAIMED · ${seats.remaining} LEFT`}
                  </div>
                )}
              </div>
              <div className="font-display text-3xl font-bold text-navy mt-1">{price}</div>
              <div className="text-xs text-foreground/70 mt-1">
                Cancel anytime · No setup fee · Includes onboarding, DMS import, unlimited SMS to your customers.
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label htmlFor="dealership_name" className="font-mono-label">Dealership name</Label>
                <Input id="dealership_name" required value={form.dealership_name} onChange={onChange("dealership_name")} placeholder="Molle AutoGroup" data-testid="signup-dealership-name" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="admin_name" className="font-mono-label">Your name</Label>
                <Input id="admin_name" value={form.admin_name} onChange={onChange("admin_name")} placeholder="James Ayers" data-testid="signup-admin-name" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="admin_email" className="font-mono-label">Work email</Label>
                <Input id="admin_email" type="email" required value={form.admin_email} onChange={onChange("admin_email")} placeholder="you@dealership.com" data-testid="signup-email" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="admin_password" className="font-mono-label">Password (10+ chars)</Label>
                <Input id="admin_password" type="password" required minLength={10} value={form.admin_password} onChange={onChange("admin_password")} data-testid="signup-password" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="dealership_phone" className="font-mono-label">Dealership phone (optional)</Label>
                <Input id="dealership_phone" value={form.dealership_phone} onChange={onChange("dealership_phone")} placeholder="+1 913 555 0100" data-testid="signup-phone" className="mt-1.5" />
              </div>
            </div>

            <Button type="submit" disabled={busy} className="w-full group" data-testid="signup-checkout-btn">
              {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting to secure checkout…</>) : (<>Continue to Stripe checkout <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" /></>)}
            </Button>

            <TextMeLinkForm />

            <p className="text-[0.7rem] text-muted-foreground text-center">
              Already have an account? <Link to="/login" className="text-navy underline">Sign in</Link>
            </p>
          </form>
          )}
        </div>

        <div className="hidden md:block relative bg-cover bg-center"
          style={{
            backgroundImage: 'linear-gradient(135deg, rgba(6,15,45,0.85), rgba(6,15,45,0.55)), url("https://images.pexels.com/photos/5864155/pexels-photo-5864155.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940")',
          }}>
          <div className="absolute bottom-10 left-10 right-10 text-white">
            <div className="font-mono-label text-[hsl(var(--gold-light))]">Your first 90 days</div>
            <div className="font-display text-5xl font-bold tracking-tighter mt-2">
              $28k+ recovered <br /> on average
            </div>
            <div className="gold-divider my-4 max-w-[120px]" />
            <div className="space-y-2 text-sm opacity-90 mt-4 max-w-sm">
              <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[hsl(var(--gold-light))]" /> Every declined RO turned into a scheduled appointment</div>
              <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[hsl(var(--gold-light))]" /> TCPA-compliant SMS · STOP handling out of the box</div>
              <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[hsl(var(--gold-light))]" /> Preowned Pipeline · Body Shop · Parts Pipeline included</div>
              <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[hsl(var(--gold-light))]" /> Cancel anytime · founding-four rate locked forever</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
